import { VulnerableUsersOverTimeReport } from '@/client/reports/types/reports.type';
import {
  calculateLineChartYAxisMax,
  createLineChartGradient,
  getChartTranslations,
  getVulnerableUsersDataMappedToLabels,
} from '@/ui/charts/charts.helper';
import {
  calculateExtendedTrendLine,
  getTimeBasedLabels,
} from '@/utils/helpers';
import {
  colorBlackMain,
  colorGrayDark,
  colorRedMain,
  platformFontFamily,
} from '@/utils/helpers/css-variables.helper';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  Title,
  Tooltip,
} from 'chart.js';
import { Chart } from 'primereact/chart';
import { Skeleton } from 'primereact/skeleton';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const ChartWrapper = styled.div`
  height: 100%;
  width: 100%;
  min-width: 800px;
`;

const ChartContainer = styled.div`
  overflow-x: scroll;
  width: 100%;
  height: 510px;
  min-height: 510px;
  position: relative;
  background: var(--white-main);
  margin-top: var(--default-padding);
`;

const StyledBarChart = styled(Chart)`
  width: 100%;
  height: 100%;
`;

type LineChartFilledProps = {
  fromDate: Date | undefined;
  toDate: Date | undefined;
  id?: string;
  reportData?: VulnerableUsersOverTimeReport[];
  isLoading?: boolean;
};

export const LineChartFilled = ({
  fromDate,
  toDate,
  id,
  reportData,
  isLoading,
}: LineChartFilledProps) => {
  const { t } = useTranslation();
  const chartRef = useRef<Chart>(null);
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});
  const [datasets, setDatasets] = useState<
    {
      label: string;
      value: VulnerableUsersOverTimeReport | null;
    }[]
  >([]);
  const [isChartDataLoading, setIsChartDataLoading] = useState(false);
  const docStyle = getComputedStyle(document.documentElement);
  const textColor = colorGrayDark(docStyle);
  const textColorSecondary = colorBlackMain(docStyle);

  const vulnerableUsersOverviewLabels = getTimeBasedLabels(
    fromDate,
    toDate,
    reportData,
    t,
  );

  useEffect(() => {
    if (!reportData || !vulnerableUsersOverviewLabels) return;
    const datasetsData = getVulnerableUsersDataMappedToLabels(
      reportData,
      vulnerableUsersOverviewLabels,
      !fromDate && !toDate,
    );
    setIsChartDataLoading(true);
    setDatasets(datasetsData);
  }, [reportData]);

  useEffect(() => {
    const destroyChart = () => {
      if (chartRef.current) {
        const chartInstance = (chartRef.current as any).chart;
        if (chartInstance) {
          chartInstance.destroy();
        }
      }
    };
    destroyChart();

    const chartData =
      datasets?.map((item: any) => {
        const value = Math.min(
          Number(item.value?.percentageRiskyUsers || 0),
          100,
        );
        return value === 0 ? null : value;
      }) || [];

    const chartTrend = calculateExtendedTrendLine(chartData);
    const gradient = createLineChartGradient(false, docStyle);
    const mainColor = colorRedMain(docStyle);
    const chartTranslations = getChartTranslations(false, t);

    const chartDatasets = [
      {
        type: 'line',
        label: chartTranslations.mainLabel,
        backgroundColor: gradient || mainColor,
        borderColor: mainColor,
        borderWidth: 0,
        data: datasets?.map((item: any) => {
          Math.min(Number(item.value?.percentageRiskyUsers || 0), 100).toFixed(
            0,
          );
        }),
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 0,
        pointBackgroundColor: mainColor,
        pointBorderColor: mainColor,
        pointStyle: 'circle',
        pointBorderWidth: 0,
      },
    ];

    chartDatasets.push({
      type: 'line',
      label: chartTranslations.trendLabel,
      borderColor: mainColor,
      borderWidth: 1,
      borderDash: [10, 5],
      fill: false,
      tension: 0,
      pointRadius: 0,
      pointHoverRadius: 0,
      spanGaps: true,
      data: chartTrend,
      showLine: true,
    } as any);

    const data = {
      labels: datasets?.map((item) => item.label),
      datasets: chartDatasets,
    };

    const options = {
      maintainAspectRatio: false,
      aspectRatio: 0.6,
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
      plugins: {
        tooltip: {
          enabled: false,
        },
        legend: {
          display: true,
          labels: {
            color: textColor,
            usePointStyle: true,
            pointStyle: 'circle',
            boxWidth: 10,
            boxHeight: 10,
            padding: 37,
            font: {
              family: platformFontFamily(docStyle),
            },
            filter: () => {
              return true;
            },
            generateLabels: (chart: ChartJS) => {
              const datasets = chart.data.datasets;
              return datasets.map((dataset: any, index: number) => ({
                text: dataset.label,
                fillStyle:
                  dataset.fill === false
                    ? 'transparent'
                    : dataset.borderColor || dataset.backgroundColor,
                strokeStyle: dataset.borderColor || dataset.backgroundColor,
                lineWidth: 1,
                lineDash: dataset.fill === false ? [2, 1] : [],
                hidden: !chart.isDatasetVisible(index),
                index,
              }));
            },
          },
          position: 'bottom' as const,
          align: 'start' as const,
          onClick: (e: any, legendItem: any, legend: any) => {
            const index = legendItem.datasetIndex;
            const chart = legend.chart;
            if (chart.isDatasetVisible(index)) {
              chart.hide(index);
              legendItem.hidden = true;
            } else {
              chart.show(index);
              legendItem.hidden = false;
            }
            chart.update();
          },
        },
        datalabels: {
          display: false,
        },
      },
      scales: {
        x: {
          ticks: {
            color: textColorSecondary,
            font: {
              family: platformFontFamily(docStyle),
            },
          },
          grid: {
            display: true,
            color: colorGrayDark(docStyle),
            lineWidth: 1,
            dash: [10, 10],
          },
          barPercentage: 0.5,
          categoryPercentage: 0.6,
          border: {
            dash: [5, 5],
            display: false,
          },
        },
        y: {
          min: 0,
          max: calculateLineChartYAxisMax(datasets, false),
          ticks: {
            color: textColorSecondary,
            stepSize: 5,
            callback: (value: number) => `${value}%`,
            font: {
              family: platformFontFamily(docStyle),
            },
          },
          grid: {
            display: true,
            color: colorGrayDark(docStyle),
            lineWidth: 1,
            dash: [10, 10],
          },
          border: {
            dash: [5, 5],
            display: false,
          },
        },
      },
      elements: {
        bar: {
          borderRadius: {
            topLeft: 5,
            topRight: 5,
          },
        },
        line: {
          borderDash: [7, 5],
        },
      },
    };

    setChartData(data);
    setChartOptions(options);
    setIsChartDataLoading(false);

    return () => {
      destroyChart();
    };
  }, [datasets]);

  useEffect(() => {
    return () => {
      if (chartRef.current) {
        const chartInstance = (chartRef.current as any).chart;
        if (chartInstance) {
          chartInstance.destroy();
        }
      }
    };
  }, []);

  return (
    <ChartContainer id={id}>
      <ChartWrapper>
        {isLoading || isChartDataLoading ? (
          <Skeleton height="100%" width="100%" />
        ) : (
          <StyledBarChart
            ref={chartRef}
            type="line"
            data={chartData}
            options={chartOptions}
            id={`${id}-line-chart`}
          />
        )}
      </ChartWrapper>
    </ChartContainer>
  );
};
